/**
 * «Оригинал» в выборе стилей, кабинет (WEB-4151, решение владельца 02.11).
 *
 * У воронки из разбора по ссылке сервер хранит слой `settings.originalLook` —
 * размеры сайта по блокам (скругления, отступы, высоты, кегли, рамки, тени),
 * снятые при создании. Выбор пака снимает с блоков слоя поля, равные слою
 * (правленые человеком остаются); выбор «Оригинала» дописывает недостающие и
 * ставит форму пака-основы слоя. Флаг выбора — авторский ключ
 * `settings.originalLookSelected`.
 *
 * Зеркало серверного `backend/src/ai-funnels/operations/original-look.ts`:
 * оба конца гоняют один файл случаев (`original-look.cases.json`, тест
 * `originalLook.cases.test.ts`). Настройки — через ту же смену пака
 * `withStylePack` (packComposition.ts). Перечня полей кабинет не держит —
 * работает по содержимому слоя.
 */
import { withStylePack } from './packComposition';

export const ORIGINAL_LOOK_CHOICE = 'original';

type Rec = Record<string, unknown>;
/** Значение слоя: число, строка или флаг (курсив отзыва, WEB-4167). */
type LayerValue = number | string | boolean;
type LayerBlocks = Record<string, Record<string, LayerValue>>;

function isLayerValue(v: unknown): v is LayerValue {
  return (
    typeof v === 'number' || typeof v === 'string' || typeof v === 'boolean'
  );
}

export interface OriginalLook {
  packId?: string;
  screens: Record<string, LayerBlocks>;
}

function isRecord(value: unknown): value is Rec {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function own(rec: object, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(rec, key);
}

/** Слой из настроек (мягко: значения — число, строка или флаг, пустое снято). */
export function readOriginalLook(settings: unknown): OriginalLook | undefined {
  if (!isRecord(settings)) return undefined;
  const layer = settings.originalLook;
  if (!isRecord(layer) || !isRecord(layer.screens)) return undefined;
  const screens: OriginalLook['screens'] = {};
  for (const [screenId, blocks] of Object.entries(layer.screens)) {
    if (!isRecord(blocks)) continue;
    const outBlocks: LayerBlocks = {};
    for (const [blockId, fields] of Object.entries(blocks)) {
      if (!isRecord(fields)) continue;
      const out: Record<string, LayerValue> = {};
      for (const [k, v] of Object.entries(fields)) {
        if (isLayerValue(v)) out[k] = v;
      }
      if (Object.keys(out).length > 0) outBlocks[blockId] = out;
    }
    if (Object.keys(outBlocks).length > 0) screens[screenId] = outBlocks;
  }
  return {
    ...(typeof layer.packId === 'string' ? { packId: layer.packId } : {}),
    screens,
  };
}

function layerBlockOf(
  layer: OriginalLook,
  screenId: unknown,
  blockId: unknown,
): Record<string, LayerValue> | undefined {
  if (typeof screenId !== 'string' || typeof blockId !== 'string')
    return undefined;
  const blocks = own(layer.screens, screenId)
    ? layer.screens[screenId]
    : undefined;
  if (!blocks || !own(blocks, blockId)) return undefined;
  return blocks[blockId];
}

/** Есть ли в содержимом хотя бы один блок слоя (иначе карточки «Оригинал» нет). */
export function hasOriginalLookBlocks(
  settings: unknown,
  screens: unknown,
): boolean {
  const layer = readOriginalLook(settings);
  if (!layer || !Array.isArray(screens)) return false;
  return screens.some(
    (s) =>
      isRecord(s) &&
      Array.isArray(s.blocks) &&
      s.blocks.some(
        (b) => isRecord(b) && layerBlockOf(layer, s.id, b.id) !== undefined,
      ),
  );
}

function sameValue(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function blockToChoice(
  block: Rec,
  layerBlock: Record<string, LayerValue>,
  toOriginal: boolean,
): Rec {
  let next: Rec | undefined;
  for (const [field, value] of Object.entries(layerBlock)) {
    if (toOriginal) {
      if (own(block, field) && block[field] !== undefined) continue;
      next = next ?? { ...block };
      next[field] = value;
    } else if (own(block, field) && sameValue(block[field], value)) {
      next = next ?? { ...block };
      delete next[field];
    }
  }
  return next ?? block;
}

export interface LookChoiceResult<S, C> {
  settings: S;
  screens: C;
  /** Сколько блоков изменилось (поле снято или возвращено). */
  changedBlocks: number;
}

/**
 * Выбор стиля с учётом оригинала. `choice` — id пака, `undefined` («Без
 * оформления») или `ORIGINAL_LOOK_CHOICE`. Нет слоя или ни одного блока слоя
 * в содержимом — ровно прежняя смена пака, экраны и флаг не трогаются (выбор
 * оригинала тогда — «ничего»). Ничего не мутирует; нечего менять — те же объекты.
 */
export function withLookChoice<S extends object, C extends readonly unknown[]>(
  settings: S,
  screens: C,
  choice: string | undefined,
): LookChoiceResult<S, C> {
  const layer = readOriginalLook(settings);
  const toOriginal = choice === ORIGINAL_LOOK_CHOICE;
  if (!layer || !hasOriginalLookBlocks(settings, screens)) {
    if (toOriginal) return { settings, screens, changedBlocks: 0 };
    return {
      settings: withStylePack(settings, choice),
      screens,
      changedBlocks: 0,
    };
  }

  const packed = withStylePack(settings, toOriginal ? layer.packId : choice);
  // Нет флага — «не выбран»: повтор того же пака у такой воронки — «ничего».
  const selected = (packed as Rec).originalLookSelected === true;
  const nextSettings =
    selected === toOriginal
      ? packed
      : ({ ...packed, originalLookSelected: toOriginal } as S);

  let changedBlocks = 0;
  let screensChanged = false;
  const nextScreens = screens.map((s) => {
    if (!isRecord(s) || !Array.isArray(s.blocks)) return s;
    let blocksChanged = false;
    const blocks = (s.blocks as unknown[]).map((b) => {
      if (!isRecord(b)) return b;
      const lb = layerBlockOf(layer, s.id, b.id);
      if (!lb) return b;
      const nb = blockToChoice(b, lb, toOriginal);
      if (nb !== b) {
        changedBlocks += 1;
        blocksChanged = true;
      }
      return nb;
    });
    if (!blocksChanged) return s;
    screensChanged = true;
    return { ...s, blocks };
  });

  return {
    settings: nextSettings,
    screens: (screensChanged ? nextScreens : screens) as C,
    changedBlocks,
  };
}
